import React, { FC } from "react";
import { useRouter } from "next/router";
import styled from "styled-components";
import { Drawer } from "../../components/Drawer";
import { Close } from "../../icons/Close";
import useTranslateMessage from "../../hooks/useTranslateMessage";
import { styledSpace } from "../../styles/theme";
import { NotificationsSection } from "./components/NotificationsSection";

const SettingsDrawer = styled(Drawer)`
  && {
    width: min(406px, 100vw);
    min-height: 100vh;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    font-family: "Assistant", sans-serif;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightgrey};
`;

const Title = styled.h2`
  font-size: 17px;
  font-weight: 800;
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.lightgrey};
  cursor: pointer;
`;

const UserCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 28px;
  min-height: 91px;
  box-sizing: border-box;
  padding: 16px;
  background: ${({ theme }) => theme.colors.lightgrey}4d;
  border-radius: 14px;
`;

const UserCardAvatar = styled.div`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #111111;
  color: ${({ theme }) => theme.colors.yellow};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 15px;
  text-transform: uppercase;
`;

const UserCardMeta = styled.div`
  flex: 1;
`;

const UserCardName = styled.div`
  font-weight: 700;
  font-size: 14px;
`;

const UserCardCompany = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey};
`;

const RolePill = styled.span`
  display: inline-block;
  margin-block-start: ${styledSpace(1)};
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue};
  background: ${({ theme }) => theme.colors.blue}22;
  border-radius: 999px;
`;

const Section = styled.div`
  margin: 0 28px 20px;
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 700;
  margin-block-end: 4px;
`;

const LanguageSegment = styled.div`
  display: flex;
  height: 40px;
  box-sizing: border-box;
  padding: 2px;
  background: ${({ theme }) => theme.colors.lightgrey}66;
  border-radius: 8px;
`;

const LanguageOption = styled.button<{ $active: boolean }>`
  font-family: inherit;
  flex: 1;
  padding: ${styledSpace(2)} 0;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.white : "transparent"};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.dark : theme.colors.grey};
`;

const Footer = styled.div`
  margin: auto 6px 0;
  padding-block-start: 32px;
`;

const LogoutButton = styled.button`
  font-family: inherit;
  width: 100%;
  height: 59px;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.lightgrey};
  border-radius: 8px;
  background: none;
  color: ${({ theme }) => theme.colors.red};
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
`;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
  userName?: string;
  initials: string;
  companyName?: string;
  roleLabel?: string;
}

const UserSettingsDrawer: FC<Props> = ({
  isOpen,
  onClose,
  onLogout,
  userName,
  initials,
  companyName,
  roleLabel,
}) => {
  const t = useTranslateMessage();
  const router = useRouter();

  const handleLocaleChange = (locale: string) => {
    router.push(router.asPath, undefined, { locale });
  };

  return (
    <SettingsDrawer isOpen={isOpen} onClose={onClose} width="wide">
      {/* In RTL flex: title (first child) = right, close button (second) = left */}
      <Header>
        <Title>{t({ id: "userSettingsDrawer-title" })}</Title>
        <CloseButton
          onClick={onClose}
          aria-label={t({ id: "userSettingsDrawer-closeLabel" })}
          data-cy="user-settings-close"
        >
          <Close width={16} height={16} />
        </CloseButton>
      </Header>

      <UserCard>
        <UserCardAvatar>{initials}</UserCardAvatar>
        <UserCardMeta>
          <UserCardName>{userName}</UserCardName>
          {companyName && <UserCardCompany>{companyName}</UserCardCompany>}
          {roleLabel && <RolePill>{roleLabel}</RolePill>}
        </UserCardMeta>
      </UserCard>

      <Section>
        <SectionLabel>
          {t({ id: "userSettingsDrawer-languageLabel" })}
        </SectionLabel>
        <LanguageSegment>
          <LanguageOption
            type="button"
            $active={router.locale === "en"}
            onClick={() => handleLocaleChange("en")}
            data-cy="user-settings-locale-en"
          >
            English
          </LanguageOption>
          <LanguageOption
            type="button"
            $active={router.locale === "he"}
            onClick={() => handleLocaleChange("he")}
            data-cy="user-settings-locale-he"
          >
            עברית
          </LanguageOption>
        </LanguageSegment>
      </Section>

      <NotificationsSection />

      <Footer>
        <LogoutButton onClick={onLogout} data-cy="user-settings-logout">
          {t({ id: "headerUserMenu-logoutButton" })}
        </LogoutButton>
      </Footer>
    </SettingsDrawer>
  );
};

export { UserSettingsDrawer };
